import { useEffect, useRef, useState } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  SignInPageBlueprint,
  SignInPageProps,
} from '@backstage/plugin-app-react';
import { SignInPage } from '@backstage/core-components';
import { googleAuthApiRef } from '@backstage/core-plugin-api';
import CloudOutlinedIcon from '@material-ui/icons/CloudOutlined';
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined';
import SpeedOutlinedIcon from '@material-ui/icons/SpeedOutlined';
import CircularProgress from '@material-ui/core/CircularProgress';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { BrandMark } from './nav/BrandMark';
import { lightTokens } from './theme/rafayTheme';

const googleProvider = {
  id: 'google-auth-provider',
  title: 'Google',
  message: '',
  apiRef: googleAuthApiRef,
};

// Brand tokens imported from modules/theme/rafayTheme.ts (not hand-copied
// hex values, unlike the dark-era version of this file) — this component's
// markup is still a heavily hand-styled one-off (see the CSS overrides
// below), not a normal themed MUI tree, so it can't just read
// `useTheme()`, but there's no reason for its colors to drift from the
// theme's own tokens now that both exist.
const {
  canvasBg: CANVAS_BG,
  border: BORDER,
  textPrimary: TEXT_PRIMARY,
  textSecondary: TEXT_SECONDARY,
  teal: TEAL,
  tealDark: TEAL_DARK,
  tealSoftBg: TEAL_SOFT_BG,
} = lightTokens;

const FEATURES = [
  {
    icon: <CloudOutlinedIcon fontSize="small" />,
    label: 'Provision OCI VMs in minutes',
  },
  {
    icon: <VerifiedUserOutlinedIcon fontSize="small" />,
    label: 'Built-in approval workflow',
  },
  {
    icon: <SpeedOutlinedIcon fontSize="small" />,
    label: 'Full audit trail & role-based access',
  },
];

const SignInPageContent = (props: SignInPageProps) => {
  const [isSigningIn, setIsSigningIn] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const handleClickCapture = () => {
    setIsSigningIn(true);
    // Safety net: if the OAuth popup is closed without completing sign-in,
    // don't leave the button stuck in a "signing in" state forever.
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setIsSigningIn(false), 15000);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        background: CANVAS_BG,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        animation: 'rafaySignInFadeIn 0.4s ease both',
      }}
    >
      <style>{`
        @keyframes rafaySignInFadeIn { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: translateY(0) } }

        @media (max-width: 900px) {
          .rafay-signin-brand-panel { display: none !important; }
        }

        /* Collapse Backstage Page (sets min-height:100vh internally) */
        .rafay-signin [class*="Page"],
        .rafay-signin [class*="page"] {
          min-height: 0 !important;
          height: auto !important;
          flex: 0 0 auto !important;
          overflow: visible !important;
        }
        /* Collapse Content / Header layout wrappers */
        .rafay-signin main,
        .rafay-signin header,
        .rafay-signin [class*="Content"],
        .rafay-signin [class*="content"] {
          min-height: 0 !important;
          height: auto !important;
          padding: 0 !important;
          margin: 0 !important;
          flex: 0 0 auto !important;
        }

        /* Nuclear: kill ALL backgrounds, force center alignment */
        .rafay-signin * {
          background: transparent !important;
          background-color: transparent !important;
          box-shadow: none !important;
          border: none !important;
          text-align: center !important;
        }

        /* Collapse ALL structural padding / margin — and center children */
        .rafay-signin .MuiCard-root,
        .rafay-signin .MuiPaper-root,
        .rafay-signin .MuiCardContent-root,
        .rafay-signin .MuiCardHeader-root,
        .rafay-signin .MuiBox-root,
        .rafay-signin .MuiGrid-root,
        .rafay-signin .MuiGrid-item,
        .rafay-signin .MuiGrid-container {
          padding: 0 !important;
          margin: 0 !important;
          min-height: 0 !important;
          height: auto !important;
          width: 100% !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: center !important;
          align-items: center !important;
        }

        /* Hide Backstage headers and all generated text. The [class*=...]
           rules matched MUI's dev-mode class names (e.g. BackstageHeader-
           header-1) but silently stopped working in the production build,
           where those get minified to opaque hashes — data-backstage-core-
           header is a real DOM attribute Backstage's Header always sets,
           so it survives minification. */
        .rafay-signin [data-backstage-core-header],
        .rafay-signin [class*="header"],
        .rafay-signin [class*="Header"],
        .rafay-signin .MuiCardHeader-root,
        .rafay-signin .MuiTypography-root,
        .rafay-signin p { display: none !important; }

        /* Google-style button — targets MUI class AND plain button/anchor */
        .rafay-signin .MuiButton-root,
        .rafay-signin .MuiButtonBase-root,
        .rafay-signin button,
        .rafay-signin a[role="button"] {
          background: #ffffff !important;
          background-color: #ffffff !important;
          color: #1f1f1f !important;
          border: 1px solid #dadce0 !important;
          width: 100% !important;
          height: 50px !important;
          min-height: 50px !important;
          padding: 0 20px !important;
          font-size: 14px !important;
          font-weight: 500 !important;
          letter-spacing: 0.25px !important;
          border-radius: 24px !important;
          justify-content: center !important;
          cursor: pointer !important;
          display: ${isSigningIn ? 'none' : 'flex'} !important;
          align-items: center !important;
          gap: 10px !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.12) !important;
          transition: box-shadow 0.2s, background 0.2s !important;
          text-transform: none !important;
          text-decoration: none !important;
          margin: 0 auto !important;
          flex-direction: row !important;
        }
        .rafay-signin .MuiButton-root:hover,
        .rafay-signin button:hover,
        .rafay-signin a[role="button"]:hover {
          background: #f8fffe !important;
          background-color: #f8fffe !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.18) !important;
        }
        .rafay-signin .MuiButton-root::before,
        .rafay-signin button::before,
        .rafay-signin a[role="button"]::before {
          content: '';
          display: inline-block !important;
          width: 20px !important;
          height: 20px !important;
          flex: 0 0 20px !important;
          min-height: 20px !important;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'%3E%3Cpath fill='%23EA4335' d='M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z'/%3E%3Cpath fill='%234285F4' d='M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z'/%3E%3Cpath fill='%23FBBC05' d='M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z'/%3E%3Cpath fill='%2334A853' d='M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.36-8.16 2.36-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z'/%3E%3C/svg%3E") !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }
      `}</style>

      {/* Brand panel — hidden below 900px, sign-in card takes over the
          whole viewport on mobile. */}
      <div
        className="rafay-signin-brand-panel"
        style={{
          position: 'relative',
          flex: '1 1 50%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: '40px 80px',
          overflow: 'auto',
          background: CANVAS_BG,
        }}
      >
        <div style={{ position: 'relative', marginBottom: 40 }}>
          <BrandMark size={44} withText />
        </div>
        <h1
          style={{
            position: 'relative',
            margin: 0,
            fontSize: 38,
            lineHeight: 1.2,
            fontWeight: 700,
            color: TEXT_PRIMARY,
            maxWidth: 440,
          }}
        >
          Your gateway to{' '}
          <span
            style={{
              background: `linear-gradient(90deg, ${TEAL}, #4FBEB0)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            cloud infrastructure
          </span>
        </h1>
        <p
          style={{
            position: 'relative',
            margin: '16px 0 36px',
            fontSize: 15,
            lineHeight: 1.6,
            color: TEXT_SECONDARY,
            maxWidth: 400,
          }}
        >
          Provision, track, and manage Oracle Cloud Infrastructure virtual
          machines through one governed self-service portal.
        </p>
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {FEATURES.map(feature => (
            <div
              key={feature.label}
              style={{ display: 'flex', alignItems: 'center', gap: 12 }}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  color: TEAL_DARK,
                  border: `1px solid ${BORDER}`,
                  background: TEAL_SOFT_BG,
                  flex: '0 0 auto',
                }}
              >
                {feature.icon}
              </span>
              <span style={{ fontSize: 14, color: TEXT_SECONDARY }}>
                {feature.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Sign-in card */}
      <div
        style={{
          flex: '1 1 50%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          overflow: 'auto',
        }}
      >
        <div
          style={{
            background: lightTokens.surfaceBg,
            border: `1px solid ${BORDER}`,
            borderRadius: 16,
            padding: '48px 52px',
            width: '100%',
            maxWidth: 440,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxShadow: '0 4px 20px rgba(21,33,47,0.06)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: '50%',
              border: `1px solid ${BORDER}`,
              marginBottom: 22,
            }}
          >
            <BrandMark size={40} />
          </div>
          <h2
            style={{
              margin: '0 0 6px',
              fontSize: 22,
              fontWeight: 700,
              color: TEXT_PRIMARY,
              textAlign: 'center',
            }}
          >
            Welcome back
          </h2>
          <p
            style={{
              margin: '0 0 28px',
              fontSize: 13,
              color: TEXT_SECONDARY,
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            Sign in with your Google account to continue to Control Tower
          </p>

          <div
            className="rafay-signin"
            style={{ width: '100%', position: 'relative', minHeight: 50 }}
            onClickCapture={handleClickCapture}
          >
            <SignInPage {...props} providers={[googleProvider]} />
            {isSigningIn && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  borderRadius: 24,
                  border: `1px solid ${BORDER}`,
                  background: 'rgba(255,255,255,0.85)',
                }}
              >
                <CircularProgress
                  size={18}
                  thickness={5}
                  style={{ color: TEAL }}
                />
                <span style={{ fontSize: 14, color: TEXT_SECONDARY }}>
                  Signing you in…
                </span>
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11.5,
              color: TEXT_SECONDARY,
            }}
          >
            <LockOutlinedIcon style={{ fontSize: 13 }} />
            Secure authentication powered by Google
          </div>
        </div>

        <p
          style={{
            marginTop: 24,
            fontSize: 12,
            color: TEXT_SECONDARY,
            textAlign: 'center',
          }}
        >
          © {new Date().getFullYear()} RAFAY Control Tower
          {/* Plain text, not links — this app has no Privacy Policy or
              Terms of Service pages to send them to. Shown for the same
              footer layout the reference design uses, without pretending
              either destination exists. */}
          <span style={{ margin: '0 8px' }}>|</span>
          Privacy Policy
          <span style={{ margin: '0 8px' }}>|</span>
          Terms of Service
        </p>
      </div>
    </div>
  );
};

export const signInPageModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    SignInPageBlueprint.make({
      params: {
        loader: async () => props => <SignInPageContent {...props} />,
      },
    }),
  ],
});
